import { ReactComponent as IconDownloadAll } from "@/assets/svgs/download-all-folder.svg";
import { assetUrl } from '@/utils/asset-url';
import { ReactComponent as ETRRecordDataIcon } from '@/assets/svgs/etr-record-data-icon.svg';
import { ReactComponent as WarningIndicator } from '@/assets/svgs/warning-indicator.svg';
import Button from '@/components/Button';
import DataElementsList from '@/components/DataElementsList/index.js';
import FormRenderer from "@/components/EFormV2/EFormRenderer.jsx";
import SplitFileType from '@/components/SplitFileType/index';
import Toast2 from '@/components/Toast2';
import CardConfigure from '@/componentsDS/Modal/CardConfigure';
import Modal from '@/componentsDS/Modal/Modal';
import Stepper from "@/componentsDS/Stepper/Stepper.jsx";
import { featureConstant } from "@/constants/configuration/feature.js";
import useEForm from "@/hooks/use-eform/index";
import useModal from '@/hooks/use-modal/index';
import usePage from '@/hooks/use-page';
import useShareDataElement from '@/hooks/use-share-data-element/index';
import useStepList from '@/hooks/use-step-list';
import { useLocation, useNavigate } from '@/lib/router-dom';
import { ReadCSV, ReadJSON, ValidateAttachmentsFunction, ValidateDataFunction } from '@/pages/shared-data/share-data-functions';
import api from '@/services/api/dataElements.js';
import API_ETR from '@/services/api/etr-issuance.js';
import API_FILES from '@/services/api/files.js';
import "@/translations/i18n";
import transformPayloadAttachments from "@/utils/attachment-transformer.js";
import addFormats from "@/utils/custom-ajv-formats";
import { feature, featureV2 } from "@/utils/feature-verifier.js";
import generateFileTypeErrorMessage from "@/utils/generate-file-type-error-message.js";
import rem from '@/utils/rem';
import Ajv from "ajv";
import jschardet from 'jschardet';
import { isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import IframeDetails from '../e-transferable-record/components/iframe-details/iframeDetails.jsx';
import ETRFileUploadComponent from './etr-attachments.jsx';
import RemarksComponent from "./record-details/components/remarks-component.jsx";
import TokenRegistryDropdown from "./record-details/components/token-registry-version.jsx";
import RecordDetailsSection from './record-details/index.jsx';

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);
export const pdfBasedBL = ['external_transport_document'];

const RecordTransferSharePage = ({
    defaultValidForm = false,
    defaultSetOwnerAddress = null,
    defaultSetHolderAddress = null,
    defaultSetSelectedNetwork = null,
    defaultTokenRegistryVersion = {},
    defaultRemarks = "",
    defaultFileLists = []
}) => {

    /*=================================================
    General
    /================================================*/
    const pageTitle = "Issue New ETR";
    const { goTo } = useNavigate();
    const { currentState } = useLocation();
    const [isFileUploaded, setIsFileUploaded] = useState(false);

    const shareDataElementState = useSelector(state => state.shareDataElement)
    const dexFeatures = useSelector(state => state.auth?.features)
    const isETROAToW3CEnabled = featureV2('ETR_OA_TO_W3C', dexFeatures)

    const { breadcrumbsItems } = usePage({ title: pageTitle, breadcrumbs: ["E-Transferable Record", pageTitle] })
    const { visible: cancelModal, show: showCancelModal, hide: hideCancelModal } = useModal()
    const { visible: previousModal, show: showPreviouslModal, hide: hidePreviousModal } = useModal()
    const { visible: validationErrorModal, show: showValidationErrorModal, hide: hideValidationErrorModal } = useModal()
    const [validationFailures, setValidationFailures] = useState([])
    const [fileLists, setFileLists] = useState(defaultFileLists);
    const [selectedTabForView, setSelectedTabForView] = useState("assetData");

    const [ownerAddress, setOwnerAddress] = useState(defaultSetOwnerAddress);
    const [holderAddress, setHolderAddress] = useState(defaultSetHolderAddress);
    const [isSameOwnerHolder, setIsSameOwnerHolder] = useState(false);
    const [isSameOwnerHolderDisabled, setIsSameOwnerHolderDisabled] = useState(false);
    const [selectedNetwork, setSelectedNetwork] = useState(defaultSetSelectedNetwork);
    const [tokenRegistryVersion, setTokenRegistryVersion] = useState(defaultTokenRegistryVersion);
    const [remarks, setRemarks] = useState(defaultRemarks);
    const [selectedModelTerms, setSelectedModelTerms] = useState(null);
    const iframeRef = useRef();
    const [submitLoading, setSubmitLoading] = useState(false)

    const isETRLegalRequirementsFeatureEnabled = feature(featureConstant?.IG_PNI_LEGAL_REQUIREMENTS);

    /*=================================================
    Share Data Element Hook
    /================================================*/
    const {
        handleDataElement,
        handleReset
    } = useShareDataElement()

    /*=================================================
        API
    /================================================*/
    const { ETRDataElements } = useSelector(state => state.staticData.data)
    const ETRDataElementItems = ETRDataElements?.map((item) => {
        return {
            ...item?.de,
            id: item.dataElementId,
            name: item?.de?.elementName,
            schema: item?.de?.elementSchema,
        }
    })

    /*=================================================
        Steps
    /================================================*/
    const stepArr = [
        { stepTitle: "Select Record" },
        { stepTitle: "Fill Record Data" },
        { stepTitle: "Review Record" }
    ]
    const steps = [...stepArr];
    const [stepSelected, setStepSelected] = useState(["", "", ""])
    const { step, prevStep, nextStep } = useStepList(steps)
    const [files, setFiles] = useState([])
    const [readOnly, setReadOnly] = useState(false)
    const isETRIGPNIFeatureEnabled = feature(featureConstant?.ETR_IGPNI);
    const isETRIGPNITRV5FeatureEnabled = feature(featureConstant?.ETR_IGPNI_TR_V5);

    const [error, setError] = useState()
    const handleError = () => {
        setError()
    }

    const onDropPayload = useCallback(acceptedFile => {
        setSubmitLoading(true);

        acceptedFile?.forEach(file => {
            const filereader = new FileReader()
            filereader.onload = () => {
                setFiles([])
                const result = filereader.result
                const fileEncoding = jschardet.detect(result).fileEncoding;
                if (fileEncoding && fileEncoding?.toLowerCase() !== 'utf-8' && fileEncoding?.toLowerCase() !== 'ascii') {
                    setError("Error: Invalid file type, please convert to UTF-8 encoding.");
                    setSubmitLoading(false);
                    return
                }
                try {
                    setFiles([...files, Object.assign(file, {
                        preview: URL.createObjectURL(file)
                    })]);
                    setIsFileUploaded(true);
                } catch (error) {
                    setError("Error: Invalid JSON file format. Please check the file content.");
                    setSubmitLoading(false);
                    return;
                }
            }
            filereader.readAsBinaryString(file)
            setSubmitLoading(false);
        })
    }, [])

    const onDropRejectedPayload = useCallback(_rejectedFiles => {
        return setError(generateFileTypeErrorMessage(['.json']));
    }, []);

    const { getRootProps: getRootPropsPayload, getInputProps: getInputPropsPayload } = useDropzone({
        onDrop: onDropPayload,
        onDropRejected: onDropRejectedPayload,
        multiple: false,
        accept: `${'application/json, text/csv, application/vnd.ms-excel'
            }`,
    });

    async function handleNext() {
        if (step === 1) {
            if (isETROAToW3CEnabled) {
                setSubmitLoading(true)
                const [err] = await API_ETR.validateSchema(shareDataElementState?.dataElement?.id)
                setSubmitLoading(false)
                if (err) {
                    const failures = err?.response?.data?.data?.failures || err?.response?.data?.failures
                    if (failures?.length > 0) {
                        setValidationFailures(failures.map(f => f.message || f.reason))
                    } else {
                        setValidationFailures([err?.message || 'An unexpected error occurred. Please try again or contact support.'])
                    }
                    showValidationErrorModal()
                    return
                }
            }
            setStepSelected((currentArray) => {
                currentArray[0] = shareDataElementState?.dataElement?.name
                return currentArray
            })
        }
        if (step === 2) {
            setStepSelected((currentArray) => {
                currentArray[1] = "Completed"
                return currentArray
            })
        }
        nextStep()
    }

    function handlePrevious() {
        if (step === 2) {
            showPreviouslModal()
        }
        else {
            prevStep()
        }
    }

    function handleResetAllFunction() {
        handleRemoveFile()
        setFileLists([])
        setPayload([])
        setOwnerAddress(null)
        setHolderAddress(null)
        setSelectedNetwork(null)
        setTokenRegistryVersion(null)
        setRemarks("")
        prevStep()
        setSelectedModelTerms(null)
    }

    async function handleSubmit() {
        let body = {
            ownerAddress: ownerAddress,
            holderAddress: holderAddress,
            networkId: parseInt(selectedNetwork?.networkId),
            dataElementId: shareDataElementState?.dataElement?.id,
            payload: payload,
            attachments: fileLists
        };

        if (isETRIGPNITRV5FeatureEnabled && tokenRegistryVersion.id === "V5") {
            body["tokenRegistryVersion"] = tokenRegistryVersion.id;
            body["remarks"] = remarks.trim();
        }

        if (isETRIGPNITRV5FeatureEnabled && tokenRegistryVersion.id != "V5") {
            body["tokenRegistryVersion"] = tokenRegistryVersion.id;
        }
        if (submitLoading) {
            return
        }

        setSubmitLoading(true)

        if (body.attachments.length > 0) {
            const promises = [];
            const promise = (async () => {
                const [error, uploadFilesResult] = await API_FILES.upload(body.attachments);
                if (error) {
                    setSubmitLoading(false)
                    setError(error?.error?.message || error?.message || "Unable to submit push request");
                    return;
                }
                body["attachments"] = uploadFilesResult?.reduce((pre, cur) => [...pre, ...cur.data.data], []);
            })();
            promises.push(promise);
            await Promise.all(promises);
        }

        const [error] = await API_ETR.submitAPI(body);
        if (error) {
            setSubmitLoading(false);
            setError(error?.error?.message || error?.message || "Unable to submit push request");
            return;
        }
        setSubmitLoading(false);
        return goTo('/record-transfer/share/success');
    }

    /*=================================================
        JSON & CSV Template
    /================================================*/
    const [jsonPath, setJsonPath] = useState(null)
    const [csvPath, setCsvPath] = useState(null)

	useEffect(() => {
		let isMounted = true;
		const validateAccess = async () => {
			if (isMounted && (!currentState || !currentState?.hasTermClause)) {
				goTo("/record-transfer");
			}
		};

		if (isETRLegalRequirementsFeatureEnabled) {
			validateAccess();
		}

		return () => {
			isMounted = false;
		};
	}, [currentState]);

    useEffect(() => {
        if (shareDataElementState?.dataElement?.id) {
            getTemplate(shareDataElementState?.dataElement.id)
        }

        async function getTemplate(id) {

            const [error, response] = await api.getDataElementTemplate(id)

            if (error) return setError(error?.error?.message || "Unable to retrieve JSON template")

            const jsonPath = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(response.jsonTemplate))}`

            setJsonPath(jsonPath)
            setCsvPath(`data:text/csv;charset=utf-8,${response.csvTemplate}`)
        }

    }, [shareDataElementState?.dataElement])

    useEffect(() => {
        if (!currentState || currentState.type === "new") {
            handleReset()
            return;
        }
    }, [currentState]);

    const {
        payload,
        setPayload,
        getFullJson,
        extractCombinedSchema,
        getSchemaWithoutAttachment
    } = useEForm();

    const [schema, setSchema] = useState(null);

    useEffect(() => {
        const elementSchema = shareDataElementState?.dataElement?.schema;
        let { payload } = shareDataElementState;

        if (Array.isArray(payload)) {
            payload = payload[0];
        }

        const plainSchema = extractCombinedSchema(
            elementSchema,
            payload
        );

        const schemaWithoutAttachment = getSchemaWithoutAttachment(plainSchema, shareDataElementState?.dataElement?.id);

        setSchema(schemaWithoutAttachment);
        setPayload(getFullJson(schemaWithoutAttachment, payload));
    }, [shareDataElementState?.dataElement?.id, files, step === 1]);

    useEffect(async () => {
        if (files.length === 0) {
            return
        }
        handleFileUpload()
    }, [files.length, step])

    useEffect(async () => {
        if (isETRIGPNIFeatureEnabled && pdfBasedBL.includes(shareDataElementState?.dataElement?.id)) {
            switch (step) {
                case 2:
                    setPayload(await transformPayloadAttachments(payload, true));
                    break;
                case 3:
                    setPayload(await transformPayloadAttachments(payload, false));
                    break;
                default:
                    break;
            }
        }
    }, [step])

    const handleRemoveFile = () => {
        setFiles([])
        setReadOnly(false)
        setIsFileUploaded(false)
    }

    const handleFileUpload = async () => {
        setSubmitLoading(true)
        if (files.length > 0) {
            let json;
            const file = files[0];

            if (file.type === "application/json") {
                json = await ReadJSON(file);
            } else if (file.type === "text/csv" || file.type === "application/vnd.ms-excel") {
                json = await ReadCSV(file, schema);
            } else {
                handleRemoveFile();
                setSubmitLoading(false);
                return;
            }

            if (!Array.isArray(json)) {
                setError("Error: Invalid file content. Expected an array.");
                handleRemoveFile();
                setSubmitLoading(false);
                return;
            }
            const updatedPayload = json?.map(({ attachments, ...rest }) => { return rest });
            if (!ValidateDataFunction(updatedPayload, schema) || ValidateAttachmentsFunction(updatedPayload)) {
                if (shareDataElementState?.dataElement?.name) {
                    let errMsg = "Please upload the correct .JSON or .CSV file.";
                    setError(`Invalid data uploaded for ${shareDataElementState?.dataElement.name}. ${errMsg}`);
                } else {
                    setError("Unable to upload the file. Please check the file format and try again.")
                }
                setSubmitLoading(false);
                handleRemoveFile();
                return;
            }

            setPayload(updatedPayload[0]);
            setReadOnly(true)
        } else {
            handleRemoveFile();
        }
        setSubmitLoading(false);
    }

    /*=================================================
        Submit Function
    /================================================*/
    const [isValidForm, setIsValidForm] = useState(defaultValidForm);
    const [isValidFormat, setIsValidFormat] = useState(true);

    useEffect(() => {
        if (payload) {
            const validate = ajv.compile(schema);
            const { attachments, ...payloadToBeValidate } = payload;
            const isPayloadValid = validate(payloadToBeValidate);
            setIsValidForm(isValidFormat && isPayloadValid);
            return isPayloadValid;
        }
    }, [payload]);

    const onSetSameOwnerHolder = () => {
        if (!isSameOwnerHolder) {
            if (ownerAddress) {
                setHolderAddress(ownerAddress)
            } else if (holderAddress) {
                setOwnerAddress(holderAddress)
            }
        }
        setIsSameOwnerHolder(!isSameOwnerHolder);
    }

    const onSetHolderAddress = (address) => {
        setHolderAddress(address);
        if (isSameOwnerHolder) {
            setOwnerAddress(address);
        }

        if (!isSameOwnerHolder && ownerAddress && !isEqual(ownerAddress, address)) {
            setIsSameOwnerHolderDisabled(true);
        } else {
            setIsSameOwnerHolderDisabled(false);
        }
    }

    const onSetOwnerAddress = (address) => {
        setOwnerAddress(address);
        if (isSameOwnerHolder) {
            setHolderAddress(address);
        }

        if (!isSameOwnerHolder && holderAddress && !isEqual(holderAddress, address)) {
            setIsSameOwnerHolderDisabled(true);
        } else {
            setIsSameOwnerHolderDisabled(false);
        }
    }


    return (
        <>
            <div className="etr-title-header">
                <div className="title-header__left">
                    <div className="title-header__title">
                        {pageTitle}
                    </div>
                </div>
                <div className="title-header__right"></div>
            </div>

            <div className="layout-default__full-width--padded">
                <Stepper steps={steps} currentStep={step - 1} />
            </div>

            <hr className='etr-hr' />

            {step === 1 && (
                <>
                    <span className="t--share-data-subheader">Choose a transferable record to issue:</span>
                    <div className="data-element-selectors">
                        <div className="data-element-selectors__cols">
                            <DataElementsList items={ETRDataElementItems} selected={shareDataElementState?.dataElement} onChange={(e) => handleDataElement(e)} />
                        </div>
                    </div>
                </>
            )}

            {step === 2 && (
                <>
                    <RecordDetailsSection
                        ownerAddress={ownerAddress}
                        setOwnerAddress={onSetOwnerAddress}
                        holderAddress={holderAddress}
                        setHolderAddress={onSetHolderAddress}
                        selectedNetwork={selectedNetwork}
                        setSelectedNetwork={setSelectedNetwork}
                        tokenRegistryVersion={tokenRegistryVersion}
                        setTokenRegistryVersion={setTokenRegistryVersion}
                        remarks={remarks}
                        setRemarks={setRemarks}
                        isSameOwnerHolder={isSameOwnerHolder}
                        setSameOwnerHolder={onSetSameOwnerHolder}
                        isSameOwnerHolderDisabled={isSameOwnerHolderDisabled}
                    />
                    <div className="etr-data-element-payload-data-row__eform">
                        <div className='etr-data-element-payload-heading'>
                            <div className="etr-data-element-payload-heading-details">
                                <div className="etr-data-element-payload-heading-details-left">
                                    <ETRRecordDataIcon />
                                    <div>Record Data</div>
                                </div>
                                {(!isETRIGPNIFeatureEnabled || (isETRIGPNIFeatureEnabled && !pdfBasedBL.includes(shareDataElementState?.dataElement?.id))) && <div className="etr-data-element-payload-heading-details-right">
                                    <div>Download Template:</div>
                                    <div className='etr-data-element-payload-heading-details-right-upload'>
                                        <a href={jsonPath} download={`json_template_${shareDataElementState?.dataElement.id}.json`}>.JSON</a>
                                        <a href={csvPath} download={`csv_template_${shareDataElementState?.dataElement.id}.csv`}>.CSV</a>
                                    </div>
                                </div>}
                            </div>
                            {(!isETRIGPNIFeatureEnabled || (isETRIGPNIFeatureEnabled && !pdfBasedBL.includes(shareDataElementState?.dataElement?.id))) && <div className="etr-issuance-record-data-tip-flex">
                                <div className="etr-data-element-payload-heading-details-left">
                                    <IconDownloadAll />
                                    <div className="etr-issuance-record-list-tip-details">
                                        Tip: You may pre-fill form by uploading data file JSON or CSV or fill in the fields manually.
                                    </div>
                                </div>
                                <div className="etr-data-element-payload-heading-details-right" {...getRootPropsPayload()} >
                                    {files.length === 0 ?
                                        <>
                                            <Button
                                                text={"Upload .JSON / .CSV Template"}
                                                round
                                                sizing='Medium'
                                                full
                                                loading={submitLoading}
                                            />
                                            <input
                                                {...getInputPropsPayload()}
                                                data-testid="payload-dropzone"
                                            />
                                        </>
                                        :
                                        <Button
                                            text={"Reset"}
                                            round
                                            ghost
                                            sizing='Medium'
                                            full
                                            loading={submitLoading}
                                            submit={() => {
                                                setPayload(null);
                                                handleRemoveFile();
                                            }}
                                        />
                                    }
                                </div>
                            </div>}
                            {files.length > 0 &&
                                <div className='etr-issuance-record-data-tip-flex flex-start'>
                                    <div className="etr-data-element-payload-heading-details-left">
                                        <span className="etr-issuance-record-list-uploaded-file">
                                            Uploaded File:
                                        </span>
                                        <div className="title-subheader__subtitle--action">
                                            <SplitFileType text={files[0].name} />
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                        <div className='etr-issuance-eform-data'>
                            {
                                <FormRenderer
                                    payload={payload}
                                    schema={schema}
                                    isReadOnly={readOnly}
                                    setPayload={setPayload}
                                    setIsValidFormat={setIsValidFormat}
                                    selectedModelTerms={selectedModelTerms}
                                    setSelectedModelTerms={setSelectedModelTerms}
                                />
                            }
                            <div className="etr-eform-row-heading">Attachment</div>
                            <ETRFileUploadComponent fileLists={fileLists} setFileLists={setFileLists} isReadOnly={false} />
                        </div>
                    </div>
                </>
            )}

            {step === 3 && (
                <div className='etr-data-element-payload-review__eform-data'>
                    <div className="etr-data-element-payload-review__eform">
                        <div className='etr-data-element-payload-review__eform-heading'>
                            <div className='etr-data-element-payload-review-status-name'>{stepSelected[0]}: </div>
                            <div className={selectedTabForView === 'assetData' ? `etr-data-element-payload-review-status-active` : `etr-data-element-payload-review-status`} onClick={() => setSelectedTabForView("assetData")}>Asset Data</div>
                            <div className={selectedTabForView === 'attachments' ? `etr-data-element-payload-review-status-active` : `etr-data-element-payload-review-status`} onClick={() => setSelectedTabForView("attachments")}>Attachments</div>
                        </div>
                        {selectedTabForView === 'assetData' && <div className='etr-issuance-eform-data'>
                            <IframeDetails
                                ref={iframeRef}
                                payload={payload}
                                dataElementId={shareDataElementState?.dataElement?.id}
                            />
                        </div>}
                        {selectedTabForView === 'attachments' && <div className='etr-issuance-eform-data'>
                            <div className="etr-eform-row-heading">Attachment</div>
                            <ETRFileUploadComponent
                                fileLists={fileLists}
                                setFileLists={setFileLists}
                                isReadOnly={true}
                            />
                        </div>}
                    </div>
                    <div className="record_details_side_panel__flex">
                        <div className="record_details_header">
                            Record Details
                        </div>
                        {isETRIGPNITRV5FeatureEnabled && <TokenRegistryDropdown
                            tokenRegistryVersion={tokenRegistryVersion}
                            setTokenRegistryVersion={setTokenRegistryVersion}
                            selectedNetwork={selectedNetwork}
                            showDropdownValue={true}
                        />}
                        <div className="title-header__hr">
                            <hr />
                        </div>
                        <div>
                            <div className='record_details_header_view_text'>Owner:</div>
                            <div className='record_details_body_view_org'>{ownerAddress?.name}</div>
                            <div className='record_details_body_view_orgid'>{ownerAddress?.walletAddress}</div>
                        </div>
                        <div>
                            <div className='record_details_header_view_text'>Holder:</div>
                            <div className='record_details_body_view_org'>{holderAddress?.name}</div>
                            <div className='record_details_body_view_orgid'>{holderAddress?.walletAddress}</div>
                        </div>
                        {isETRIGPNITRV5FeatureEnabled && tokenRegistryVersion?.id === "V5" && <RemarksComponent
                            remarks={remarks}
                            setRemarks={setRemarks}
                            showTextAreaValue={true}
                        />}
                    </div>
                </div>
            )}
            <div className="DS__layout__footer">
                <div className="layout-default__sticky-actions">
                    <div className="layout-default__sticky-actions__left">
                        <Button text={"Cancel"}
                            sizing='Medium' round
                            full noborder maxWidth='154'
                            submit={() => showCancelModal()}
                            data-testid="cancel-button"
                        />
                    </div>
                    <div className="layout-default__sticky-actions__right" style={{ display: 'flex' }}>
                        <div className="overview__button_wrap">
                            {step > 1 &&
                                <Button text={"Previous"} round nobackground noborder
                                    sizing='Medium' ghost full maxWidth='154'
                                    submit={() => handlePrevious()}
                                    data-testid="previous-button"
                                />
                            }
                            {(step > 0 && step !== steps.length) &&
                                <Button text={"Next"} round ghost
                                    sizing='Medium' full maxWidth='154'
                                    data-testid="next-button"
                                    disabled={(isEmpty(shareDataElementState?.dataElement)) || (step === 2 && (isFileUploaded ? false : !isValidForm)) || (step === 2 && (!ownerAddress || !holderAddress || !selectedNetwork || (isETRIGPNITRV5FeatureEnabled && !tokenRegistryVersion?.id)))}
                                    submit={() => handleNext()}
                                />
                            }
                            {step === steps.length &&
                                <Button
                                    text={"Submit"} round
                                    sizing='Medium' full maxWidth='154'
                                    data-testid="submit-button"
                                    submit={() => handleSubmit()}
                                />
                            }
                        </div>
                    </div>
                </div>
            </div>

            {
                submitLoading && (
                    ReactDOM.createPortal(
                        <div className='modal white'>
                            <div className='loading'>
                                <img src={assetUrl("/loading.gif")} alt="Loading" />
                            </div>
                        </div>
                        , document.body
                    )
                )
            }

            <Toast2 show={error} errorMessage={error} title={"Error"} warningIcon={true} close={() => { handleError() }} />

            <Modal visible={previousModal} variant="medium" close={hidePreviousModal}>
                <CardConfigure >
                    <CardConfigure.Header>
                        <CardConfigure.Header.Title>
                            <div className="card-configure__header__left">
                                <div className="card-configure__title">
                                    <div className="card-configure__title__left">
                                        <div className={'card-configure__icon'}>
                                            <WarningIndicator />
                                        </div>
                                    </div>
                                    <div className="card-configure__title__right">
                                        <div className="card-configure__title__right__top">
                                            Return to Previous Step
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </CardConfigure.Header.Title>
                    </CardConfigure.Header>
                    <CardConfigure.Body>
                        <CardConfigure.Body.Col>
                            <div className='share__card-configure-text'>Are you sure to go back to previous page? Your progress will be lost.</div>
                        </CardConfigure.Body.Col>
                    </CardConfigure.Body>
                    <CardConfigure.Footer>
                        <CardConfigure.Footer.Right>
                            <Button ghost text="Cancel"
                                data-testid="previous-step-modal-cancel-button"
                                submit={() => {
                                    hidePreviousModal()
                                }} />
                            <span style={{ width: rem(10), display: 'inline-block' }}></span>
                            <Button text="Confirm"
                                data-testid="previous-step-modal-confirm-button"
                                warning
                                submit={() => {
                                    hidePreviousModal()
                                    handleResetAllFunction()
                                }}
                            />
                        </CardConfigure.Footer.Right>
                    </CardConfigure.Footer>
                </CardConfigure>
            </Modal>

            <Modal visible={validationErrorModal} variant="medium" close={hideValidationErrorModal}>
                <CardConfigure>
                    <CardConfigure.Header>
                        <CardConfigure.Header.Title>
                            <div className="card-configure__header__left">
                                <div className="card-configure__title">
                                    <div className="card-configure__title__left">
                                        <div className={'card-configure__icon'}>
                                            <WarningIndicator />
                                        </div>
                                    </div>
                                    <div className="card-configure__title__right">
                                        <div className="card-configure__title__right__top">
                                            Validation Failed
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardConfigure.Header.Title>
                    </CardConfigure.Header>
                    <CardConfigure.Body>
                        <CardConfigure.Body.Col>
                            <div className='share__card-configure-text'>
                                {validationFailures.map((message, index) => (
                                    <div key={index}>{message}</div>
                                ))}
                            </div>
                        </CardConfigure.Body.Col>
                    </CardConfigure.Body>
                    <CardConfigure.Footer>
                        <CardConfigure.Footer.Right>
                            <Button data-testid="validation-error-modal-dismiss" text="Dismiss" ghost submit={() => hideValidationErrorModal()} />
                        </CardConfigure.Footer.Right>
                    </CardConfigure.Footer>
                </CardConfigure>
            </Modal>

            <Modal visible={cancelModal} variant="medium" close={hideCancelModal}>
                <CardConfigure >
                    <CardConfigure.Header>
                        <CardConfigure.Header.Title>
                            <div className="card-configure__header__left">
                                <div className="card-configure__title">
                                    <div className="card-configure__title__left">
                                        <div className={'card-configure__icon'}>
                                            <WarningIndicator />
                                        </div>
                                    </div>
                                    <div className="card-configure__title__right">
                                        <div className="card-configure__title__right__top">
                                            Exit Share Data
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardConfigure.Header.Title>
                    </CardConfigure.Header>
                    <CardConfigure.Body>
                        <CardConfigure.Body.Col>
                            <div className='share__card-configure-text'>Are you sure you want to exit Sharing Data? Your progress will be lost.</div>
                        </CardConfigure.Body.Col>
                    </CardConfigure.Body>
                    <CardConfigure.Footer>
                        <CardConfigure.Footer.Right>
                            <Button ghost text="Cancel"
                                data-testid="exit-etr-cancel-button"
                                submit={() => {
                                    hideCancelModal()
                                }} />
                            <span style={{ width: rem(10), display: 'inline-block' }}></span>
                            <Button text="Confirm"
                                warning
                                data-testid="exit-etr-confirm-button"
                                submit={() => {
                                    hideCancelModal();
                                    goTo(`/record-transfer`);
                                }}
                            />
                        </CardConfigure.Footer.Right>
                    </CardConfigure.Footer>
                </CardConfigure>
            </Modal>
        </>
    )
}

RecordTransferSharePage.propTypes = {
    defaultValidForm: PropTypes.bool,
    defaultSetOwnerAddress: PropTypes.object,
    defaultSetHolderAddress: PropTypes.object,
    defaultSetSelectedNetwork: PropTypes.number,
    defaultTokenRegistryVersion: PropTypes.object,
    defaultRemarks: PropTypes.string,
    defaultFileLists: PropTypes.array
};

export default RecordTransferSharePage
